import type { Logger } from "@1js/office-online-telemetry";
import { LogCategory, TraceLevel } from "@1js/office-online-telemetry";
import { type DocumentInfo, type ErrorInfo, type MeetingInfo } from "../type";
import { PPTLiveFrameManager } from "./pptliveFrameManager";
import type { PresentResponse, JoinResponse } from "./pptLiveType";
import { PPTLiveEnvironment, PPTLiveEnvironmentConstants } from "./pptLiveType";
import * as Errors from "../errors";

export class PPTLiveHandler {
  private readonly loadTimeoutMs: number = 120000;

  private readonly logger: Logger;
  private readonly instanceId: string;
  private readonly iframedomain: string;
  private readonly frameManager: PPTLiveFrameManager;
  private readonly errorCallback: (error: ErrorInfo) => void;

  private presentResolve: ((x: PresentResponse) => void) | undefined;

  public constructor(
    logger: Logger,
    instanceId: string,
    environment: PPTLiveEnvironment,
    errorCallback: (error: ErrorInfo) => void
  ) {
    this.logger = logger;
    this.instanceId = instanceId;
    this.iframedomain = this.getDomainForEnvironment(environment);
    this.errorCallback = errorCallback;

    this.frameManager = new PPTLiveFrameManager(logger, instanceId);

    this.presentResolve = undefined;

    this.frameManager.addMessageListener((data: any) => {
      this.onMessage(data);
    });
  }

  public dispose(): void {
    this.frameManager.dispose();
  }

  public async present(
    container: HTMLElement,
    docInfo: DocumentInfo,
    meetingInfo: MeetingInfo,
    hostName: string,
    pageDomain: string,
    accessToken: string
  ): Promise<PresentResponse> {
    this.logger.sendTraceTag(
      0x1e3d2259 /* tag_4psjz */,
      LogCategory.msoulscat_Wac_PptLive,
      TraceLevel.Info,
      `PPTLiveHandler ${this.instanceId} present called`
    );

    if (!docInfo.documentIdentifier) {
      this.logger.sendTraceTag(
        0x1e3070d8 /* tag_4mhdy */,
        LogCategory.msoulscat_Wac_PptLive,
        TraceLevel.Warning,
        "Host did not provide either fileSiteUrl or fileId."
      );
    }

    const inputs: { name: string; value: string }[] = [];
    inputs.push({ name: "access_token", value: accessToken });
    inputs.push({
      name: "fileSiteUrl",
      value: docInfo.documentIdentifier?.siteUrl ?? "", // siteUrl is expected to always be present here, undefined scenario should not occur."
    });
    inputs.push({
      name: "fileId",
      value: docInfo.documentIdentifier?.sourceDoc ?? "", // sourceDoc is expected to always be present here, undefined scenario should not occur."
    });
    inputs.push({ name: "meetingId", value: meetingInfo.id });
    inputs.push({ name: "meetingHost", value: hostName });
    inputs.push({ name: "parentWindowOrigin", value: pageDomain });
    inputs.push({ name: "postMessageId", value: this.instanceId });

    const loadPromise = new Promise<PresentResponse>((resolve) => {
      this.presentResolve = resolve;

      window.setTimeout(() => {
        this.onPresentLoadFailed(Errors.AppLoadTimeout), this.loadTimeoutMs;
      }, this.loadTimeoutMs);

      this.frameManager
        .tryLoadIFrame(container, this.iframedomain, "present", inputs)
        .catch((ev) => {
          this.logger.sendTraceTag(
            0x1e3d2258 /* tag_4psjy */,
            LogCategory.msoulscat_Wac_PptLive,
            TraceLevel.Info,
            `PPTLiveHandler ${this.instanceId} iframe failed to load. Error: ${ev}`
          );
          this.onPresentLoadFailed(Errors.AppLoadFailedUnexpected);
        });
    });
    return loadPromise;
  }

  public async join(
    container: HTMLElement,
    joinInfo: string,
    meetingInfo: MeetingInfo,
    hostName: string,
    pageDomain: string
  ): Promise<JoinResponse> {
    this.logger.sendTraceTag(
      0x1e3d2257 /* tag_4psjx */,
      LogCategory.msoulscat_Wac_PptLive,
      TraceLevel.Info,
      `PPTLiveHandler ${this.instanceId} join called`
    );

    const inputs: { name: string; value: string }[] = [];
    inputs.push({ name: "joinInfo", value: joinInfo });
    inputs.push({ name: "meetingId", value: meetingInfo.id });
    inputs.push({ name: "meetingHost", value: hostName });
    inputs.push({ name: "parentWindowOrigin", value: pageDomain });
    inputs.push({ name: "postMessageId", value: this.instanceId });

    const loadPromise = new Promise<JoinResponse>((resolve) => {
      window.setTimeout(() => {
        resolve({
          success: false,
          error: Errors.AppLoadTimeout,
        }),
          this.loadTimeoutMs;
      });

      this.frameManager
        .tryLoadIFrame(container, this.iframedomain, "join", inputs)
        .then(() => {
          this.logger.sendTraceTag(
            0x1e3d2256 /* tag_4psjw */,
            LogCategory.msoulscat_Wac_PptLive,
            TraceLevel.Info,
            `PPTLiveHandler ${this.instanceId} iframe loaded`
          );
          resolve({ success: true });
        })
        .catch((ev: any) => {
          this.logger.sendTraceTag(
            0x1e3d2255 /* tag_4psjv */,
            LogCategory.msoulscat_Wac_PptLive,
            TraceLevel.Info,
            `PPTLiveHandler ${this.instanceId} iframe failed to load. Error: ${ev}`
          );

          resolve({
            success: false,
            error: Errors.AppLoadFailedUnexpected,
          });
        });
    });

    return loadPromise;
  }

  private getDomainForEnvironment(environment: PPTLiveEnvironment): string {
    switch (environment) {
      case PPTLiveEnvironment.dev:
        return PPTLiveEnvironmentConstants.Dev;
      case PPTLiveEnvironment.production:
        return PPTLiveEnvironmentConstants.Prod;
      default:
        throw "Invalid environment specified for PPTLive";
    }
  }

  private onMessage(data: any) {
    if (data.postMessageId !== this.instanceId) {
      this.logger.sendTraceTag(
        0x1e3cf01a /* tag_4ppa0 */,
        LogCategory.msoulscat_Wac_PptLive,
        TraceLevel.Error,
        `PPTLiveHandler ${this.instanceId} ignoring message for instanceId ${data.postMessageId}`
      );
      return;
    }

    switch (data.messageType) {
      case "PresentOnLoad":
        this.onPresentLoadSucceeded(data);
        break;
      case "OnError":
        this.onErrorMessage(data);
        break;
      default:
        break;
    }
  }

  private onPresentLoadSucceeded(data: any) {
    if (this.presentResolve) {
      this.presentResolve({
        success: true,
        joinInfo: data.joinInfo,
      });
      this.presentResolve = undefined;
    } else {
      this.logger.sendTraceTag(
        0x1e3d2254 /* tag_4psju */,
        LogCategory.msoulscat_Wac_PptLive,
        TraceLevel.Error,
        `PPTLiveHandler ${this.instanceId} PresentOnLoad message received with no presentResolve available`
      );
    }
  }

  private onPresentLoadFailed(err: ErrorInfo) {
    if (this.presentResolve) {
      this.logger.sendTraceTag(
        0x1e3d2253 /* tag_4psjt */,
        LogCategory.msoulscat_Wac_PptLive,
        TraceLevel.Info,
        `PPTLiveHandler ${this.instanceId} failing for ${JSON.stringify(err)}`
      );

      this.presentResolve({
        success: false,
        error: err,
      });
      this.presentResolve = undefined;
    }
  }

  private onErrorMessage(data: any) {
    const error: ErrorInfo = {
      errorName: data.errorName,
      errorType: data.errorType,
      errorMessage: data.errorMessage,
    };
    this.logger.sendTraceTag(
      0x1e3d2252 /* tag_4psjs */,
      LogCategory.msoulscat_Wac_PptLive,
      TraceLevel.Error,
      `PPTLiveHandler ${this.instanceId} onError message. ${JSON.stringify(
        error
      )}`
    );

    if (this.presentResolve) {
      this.presentResolve({
        success: false,
        error,
      });
      this.presentResolve = undefined;
    } else {
      this.errorCallback(error);
    }
  }
}
